import { useState } from "react";
import { Bar, Pie } from "react-chartjs-2";

import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  Activity,
  AlertCircle,
  ArrowUpDown,
  ArrowUpRight,
  BadgeCheck,
  CircleAlert,
  Download,
  FileWarning,
  Loader,
  Medal,
  Search,
  Send,
  ShieldAlert,
  Store,
  TrendingUp,
  Trophy,
  Truck,
  XCircle,
} from "lucide-react";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

const PackkyReportsTab = ({
  fromDate,
  setFromDate,

  toDate,
  setToDate,

  restaurantData,
  driverTaskAnalytics,
  taskStatusAnalytics,

  onApplyFilter,
  onExport,
}) => {
  // =====================================================
  // LOCAL FILTER STATES
  // =====================================================

  const [shopSearch, setShopSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");

  const [shopSortBy, setShopSortBy] = useState("tasks_desc");
  const [driverSortBy, setDriverSortBy] = useState("tasks_desc");

  // =====================================================
  // SAFE DATA
  // =====================================================

  const safeRestaurantData = (restaurantData || []).map((item) => ({
    ...item,
    shopName: item?.shopName?.trim() || "Unknown Shop",
    shopCode: item?.shopCode || "—",
    taskCount: Number(item?.taskCount || 0),
  }));

  const safeDriverTaskAnalytics = (driverTaskAnalytics || []).map((item) => ({
    ...item,
    driverName: item?.driverName?.trim() || "Unknown Driver",
    driverCode: item?.driverCode || "—",
    driverMobile: item?.driverMobile || "—",
    taskCount: Number(item?.taskCount || 0),
  }));

  const safeTaskStatusAnalytics = (taskStatusAnalytics || []).map((item) => ({
    ...item,
    taskStatus: item?.taskStatus?.trim() || "unknown",
    taskCount: Number(item?.taskCount || 0),
  }));

  // =====================================================
  // TOTALS
  // =====================================================

  const totalRestaurantTasks = safeRestaurantData.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const totalDriverTasks = safeDriverTaskAnalytics.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const totalStatusTasks = safeTaskStatusAnalytics.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const successfulTasks =
    safeTaskStatusAnalytics.find(
      (item) => item.taskStatus.toLowerCase() === "successful",
    )?.taskCount || 0;

  const cancelledTasks =
    safeTaskStatusAnalytics.find(
      (item) => item.taskStatus.toLowerCase() === "cancelled",
    )?.taskCount || 0;

  const activePipelineTasks = safeTaskStatusAnalytics
    .filter((item) =>
      [
        "acknowledged",
        "arrived",
        "food is ready",
        "assigned",
        "unassigned",
      ].includes(item.taskStatus.toLowerCase()),
    )
    .reduce((sum, item) => sum + item.taskCount, 0);

  const successRate = totalStatusTasks
    ? ((successfulTasks / totalStatusTasks) * 100).toFixed(2)
    : "0.00";

  const cancelRate = totalStatusTasks
    ? ((cancelledTasks / totalStatusTasks) * 100).toFixed(2)
    : "0.00";

  // =====================================================
  // TOP SHOPS / DRIVERS
  // =====================================================

  const topShops = [...safeRestaurantData]
    .sort((a, b) => b.taskCount - a.taskCount)
    .slice(0, 5);

  const topDrivers = [...safeDriverTaskAnalytics]
    .sort((a, b) => b.taskCount - a.taskCount)
    .slice(0, 5);

  const busiestShop = topShops[0];
  const secondBusiestShop = topShops[1];

  const top2ShopShare = totalRestaurantTasks
    ? (
        (((busiestShop?.taskCount || 0) + (secondBusiestShop?.taskCount || 0)) /
          totalRestaurantTasks) *
        100
      ).toFixed(1)
    : "0.0";

  const avgTasksPerShop = safeRestaurantData.length
    ? Math.round(totalRestaurantTasks / safeRestaurantData.length)
    : 0;

  const avgTasksPerDriver = safeDriverTaskAnalytics.length
    ? Math.round(totalDriverTasks / safeDriverTaskAnalytics.length)
    : 0;

  // =====================================================
  // UNKNOWN DATA
  // =====================================================

  const unknownShopCount = safeRestaurantData.filter(
    (item) => item.shopName === "Unknown Shop" || item.shopCode === "—",
  ).length;

  const unknownDriverCount = safeDriverTaskAnalytics.filter(
    (item) =>
      item.driverName === "Unknown Driver" ||
      item.driverCode === "—" ||
      item.driverMobile === "—",
  ).length;

  // =====================================================
  // PERFORMANCE
  // =====================================================

  const topPerformerDriver = [...safeDriverTaskAnalytics].sort(
    (a, b) => b.taskCount - a.taskCount,
  )[0];

  const lowestPerformerDriver = [...safeDriverTaskAnalytics]
    .filter((item) => item.taskCount > 0)
    .sort((a, b) => a.taskCount - b.taskCount)[0];

  // =====================================================
  // SHARE HELPERS
  // =====================================================

  const getShopShare = (count) =>
    totalRestaurantTasks
      ? ((count / totalRestaurantTasks) * 100).toFixed(1)
      : "0.0";

  const getDriverShare = (count) =>
    totalDriverTasks ? ((count / totalDriverTasks) * 100).toFixed(1) : "0.0";

  // =====================================================
  // STATUS HELPERS
  // =====================================================

  const formatStatusLabel = (value = "") =>
    value
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const getStatusTone = (status = "") => {
    const value = status.toLowerCase();

    if (["successful", "completed", "delivered"].includes(value)) {
      return {
        color: "#16a34a",
        bg: "#dcfce7",
        border: "#bbf7d0",
        Icon: BadgeCheck,
        label: "Healthy",
      };
    }

    if (
      ["cancelled", "canceled", "declined", "failed", "rejected"].includes(
        value,
      )
    ) {
      return {
        color: "#dc2626",
        bg: "#fee2e2",
        border: "#fecaca",
        Icon: XCircle,
        label: "Needs attention",
      };
    }

    if (value === "food is ready") {
      return {
        color: "#f59e0b",
        bg: "#fef3c7",
        border: "#fde68a",
        Icon: Send,
        label: "Ready",
      };
    }

    if (["assigned", "acknowledged", "arrived"].includes(value)) {
      return {
        color: "#f97316",
        bg: "#ffedd5",
        border: "#fed7aa",
        Icon: Activity,
        label: "In progress",
      };
    }

    if (value === "unassigned") {
      return {
        color: "#9333ea",
        bg: "#f3e8ff",
        border: "#e9d5ff",
        Icon: CircleAlert,
        label: "Open",
      };
    }

    return {
      color: "#64748b",
      bg: "#f1f5f9",
      border: "#e2e8f0",
      Icon: AlertCircle,
      label: "Other",
    };
  };

  const statusOrder = [
    "successful",
    "completed",
    "delivered",
    "assigned",
    "acknowledged",
    "arrived",
    "food is ready",
    "unassigned",
    "cancelled",
    "canceled",
    "declined",
    "failed",
    "rejected",
  ];

  const statusDisplayRows = [...safeTaskStatusAnalytics]
    .map((item) => {
      const percentage = totalStatusTasks
        ? ((item.taskCount / totalStatusTasks) * 100).toFixed(1)
        : "0.0";

      return {
        ...item,
        percentage,
        tone: getStatusTone(item.taskStatus),
      };
    })
    .sort((a, b) => {
      const aIndex = statusOrder.indexOf(a.taskStatus.toLowerCase());
      const bIndex = statusOrder.indexOf(b.taskStatus.toLowerCase());

      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

  const topStatus = [...statusDisplayRows].sort(
    (a, b) => b.taskCount - a.taskCount,
  )[0];

  const statusSummaryCards = [
    {
      label: "Successful",
      value: successfulTasks,
      percentage: successRate,
      Icon: BadgeCheck,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "Active Pipeline",
      value: activePipelineTasks,
      percentage: totalStatusTasks
        ? ((activePipelineTasks / totalStatusTasks) * 100).toFixed(1)
        : "0.0",
      Icon: Activity,
      color: "#f97316",
      bg: "#ffedd5",
    },
    {
      label: "Cancelled",
      value: cancelledTasks,
      percentage: cancelRate,
      Icon: XCircle,
      color: "#dc2626",
      bg: "#fee2e2",
    },
  ];

  // =====================================================
  // CHART DATA
  // =====================================================

  const restaurantPieChartData = {
    labels: topShops.map((item) => item.shopName),

    datasets: [
      {
        data: topShops.map((item) => item.taskCount),

        backgroundColor: [
          "#f97316",
          "#fb923c",
          "#fdba74",
          "#f59e0b",
          "#ea580c",
        ],

        borderWidth: 0,
      },
    ],
  };

  const driverBarChartData = {
    labels: topDrivers.map((item) => item.driverName),

    datasets: [
      {
        label: "Tasks",

        data: topDrivers.map((item) => item.taskCount),

        backgroundColor: [
          "#f97316",
          "#fb923c",
          "#fdba74",
          "#f59e0b",
          "#ea580c",
        ],

        borderRadius: 8,
        maxBarThickness: 40,
      },
    ],
  };

  // =====================================================
  // SORTING
  // =====================================================

  const sortShops = (list) => {
    const data = [...list];

    switch (shopSortBy) {
      case "tasks_asc":
        return data.sort((a, b) => a.taskCount - b.taskCount);

      case "name_asc":
        return data.sort((a, b) => a.shopName.localeCompare(b.shopName));

      case "name_desc":
        return data.sort((a, b) => b.shopName.localeCompare(a.shopName));

      case "tasks_desc":
      default:
        return data.sort((a, b) => b.taskCount - a.taskCount);
    }
  };

  const sortDrivers = (list) => {
    const data = [...list];

    switch (driverSortBy) {
      case "tasks_asc":
        return data.sort((a, b) => a.taskCount - b.taskCount);

      case "name_asc":
        return data.sort((a, b) => a.driverName.localeCompare(b.driverName));

      case "name_desc":
        return data.sort((a, b) => b.driverName.localeCompare(a.driverName));

      case "tasks_desc":
      default:
        return data.sort((a, b) => b.taskCount - a.taskCount);
    }
  };

  // =====================================================
  // FILTERED TABLE DATA
  // =====================================================

  const filteredShopRows = sortShops(
    safeRestaurantData.filter((shop) => {
      const query = shopSearch.trim().toLowerCase();

      if (!query) return true;

      return (
        shop.shopName.toLowerCase().includes(query) ||
        shop.shopCode.toLowerCase().includes(query)
      );
    }),
  );

  const filteredDriverRows = sortDrivers(
    safeDriverTaskAnalytics.filter((driver) => {
      const query = driverSearch.trim().toLowerCase();

      if (!query) return true;

      return (
        driver.driverName.toLowerCase().includes(query) ||
        driver.driverCode.toLowerCase().includes(query) ||
        driver.driverMobile.toLowerCase().includes(query)
      );
    }),
  );

  return (
    <>
      {/* =====================================================
          TOP CONTROLS
      ===================================================== */}

      <div className="mb-4 rounded-2xl border bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-11 w-full rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-11 w-full rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <button
            type="button"
            onClick={onApplyFilter}
            className="h-11 rounded-xl bg-blue-600 text-sm font-medium text-white hover:bg-blue-700"
          >
            Apply Filter
          </button>

          <button
            type="button"
            onClick={onExport}
            className="flex h-11 items-center justify-center gap-2 rounded-xl bg-green-600 text-sm font-medium text-white hover:bg-green-700"
          >
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* =====================================================
          KPI CARDS
      ===================================================== */}

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Tasks</p>

              <h3 className="mt-1 text-3xl font-bold text-gray-900">
                {totalStatusTasks}
              </h3>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50">
              <Activity className="h-5 w-5 text-orange-600" />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Status-based overall task volume
          </p>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Success Rate</p>

              <h3 className="mt-1 text-3xl font-bold text-green-600">
                {successRate}%
              </h3>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50">
              <BadgeCheck className="h-5 w-5 text-green-600" />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            {successfulTasks} successful tasks
          </p>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Cancelled</p>

              <h3 className="mt-1 text-3xl font-bold text-red-600">
                {cancelledTasks}
              </h3>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            {cancelRate}% of total status volume
          </p>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Pipeline</p>

              <h3 className="mt-1 text-3xl font-bold text-orange-600">
                {activePipelineTasks}
              </h3>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50">
              <Loader className="h-5 w-5 text-orange-600" />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Acknowledged / arrived / ready / unassigned
          </p>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Shop Concentration</p>

              <h3 className="mt-1 text-3xl font-bold text-orange-600">
                {top2ShopShare}%
              </h3>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50">
              <TrendingUp className="h-5 w-5 text-orange-600" />
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Share of top 2 shops in total shop volume
          </p>
        </div>
      </div>

      {/* =====================================================
          HERO + ATTENTION
      ===================================================== */}

      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="relative overflow-hidden rounded-[28px] border border-orange-200 bg-gradient-to-br from-[#FC981B] via-[#F59E0B] to-[#F97316] p-6 text-white shadow-[0_10px_30px_rgba(252,152,27,0.18)] md:p-7 xl:col-span-2">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" />

          <div className="relative z-10 flex items-start justify-between gap-4">
            <div>
              <p className="mb-2 text-sm font-medium tracking-wide text-white/80">
                Quick Overview
              </p>

              <h3 className="mb-3 text-2xl font-bold leading-tight md:text-3xl">
                Packky task performance overview
              </h3>

              <p className="max-w-3xl text-sm leading-7 text-white/90 md:text-[15px]">
                Delivery performance is strong with{" "}
                <span className="font-semibold text-white">{successRate}%</span>{" "}
                successful tasks and only{" "}
                <span className="font-semibold text-white">{cancelRate}%</span>{" "}
                cancellations in this range. Most volume is coming from{" "}
                <span className="font-semibold text-white">
                  {busiestShop?.shopName || "—"}
                </span>{" "}
                and{" "}
                <span className="font-semibold text-white">
                  {secondBusiestShop?.shopName || "—"}
                </span>
                , so these shops should remain the top focus for operations,
                staffing, and dispatch readiness.
              </p>
            </div>

            <div className="hidden h-16 w-16 items-center justify-center rounded-2xl border border-white/25 bg-white/15 shadow-lg backdrop-blur-md md:flex">
              <ArrowUpRight className="h-7 w-7 text-white" />
            </div>
          </div>

          <div className="relative z-10 mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="rounded-2xl border border-white/20 bg-white/14 p-4 shadow-sm backdrop-blur-md">
              <p className="text-xs uppercase tracking-wide text-white/75">
                Shops Covered
              </p>

              <p className="mt-2 text-2xl font-bold">
                {safeRestaurantData.length}
              </p>
            </div>

            <div className="rounded-2xl border border-white/20 bg-white/14 p-4 shadow-sm backdrop-blur-md">
              <p className="text-xs uppercase tracking-wide text-white/75">
                Drivers Active
              </p>

              <p className="mt-2 text-2xl font-bold">
                {safeDriverTaskAnalytics.length}
              </p>
            </div>

            <div className="rounded-2xl border border-white/20 bg-white/14 p-4 shadow-sm backdrop-blur-md">
              <p className="text-xs uppercase tracking-wide text-white/75">
                Avg / Shop
              </p>

              <p className="mt-2 text-2xl font-bold">{avgTasksPerShop}</p>
            </div>

            <div className="rounded-2xl border border-white/20 bg-white/14 p-4 shadow-sm backdrop-blur-md">
              <p className="text-xs uppercase tracking-wide text-white/75">
                Avg / Driver
              </p>

              <p className="mt-2 text-2xl font-bold">{avgTasksPerDriver}</p>
            </div>
          </div>
        </div>

        {/* ATTENTION */}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <CircleAlert className="h-5 w-5 text-orange-600" />

            <h3 className="text-lg font-semibold text-gray-900">
              Attention Points
            </h3>
          </div>

          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-3">
              <p className="font-medium text-orange-800">Top shop load</p>

              <p className="mt-1 text-orange-700">
                {busiestShop?.shopName || "—"} handles{" "}
                {busiestShop?.taskCount || 0} tasks.
              </p>
            </div>

            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
              <p className="font-medium text-amber-800">Volume concentration</p>

              <p className="mt-1 text-amber-700">
                Top 2 shops contribute {top2ShopShare}% of mapped shop volume.
              </p>
            </div>

            <div className="rounded-xl border border-red-100 bg-red-50 p-3">
              <p className="font-medium text-red-800">Cancellation watch</p>

              <p className="mt-1 text-red-700">
                {cancelledTasks} tasks are cancelled in this date range.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="font-medium text-slate-800">Data hygiene</p>

              <p className="mt-1 text-slate-600">
                {unknownShopCount} shop rows and {unknownDriverCount} driver
                rows have incomplete mapping.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          BADGES
      ===================================================== */}

      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50">
              <Medal className="h-5 w-5 text-green-600" />
            </div>

            <div>
              <p className="text-sm text-gray-500">Top Performer</p>

              <p className="text-base font-semibold text-gray-900">
                {topPerformerDriver?.driverName || "—"}
              </p>

              <p className="text-xs text-gray-500">
                {topPerformerDriver?.taskCount || 0} tasks
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50">
              <ShieldAlert className="h-5 w-5 text-orange-600" />
            </div>

            <div>
              <p className="text-sm text-gray-500">Lowest Performer</p>

              <p className="text-base font-semibold text-gray-900">
                {lowestPerformerDriver?.driverName || "—"}
              </p>

              <p className="text-xs text-gray-500">
                {lowestPerformerDriver?.taskCount || 0} tasks
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
              <FileWarning className="h-5 w-5 text-red-600" />
            </div>

            <div>
              <p className="text-sm text-gray-500">Unknown Mapping</p>

              <p className="text-base font-semibold text-gray-900">
                {unknownShopCount + unknownDriverCount} rows
              </p>

              <p className="text-xs text-gray-500">
                Incomplete shop or driver details
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          STATUS + TOP SHOPS
      ===================================================== */}

      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Activity className="h-5 w-5 text-orange-600" />
                Status Breakdown
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Clear count, percentage and operational health by status
              </p>
            </div>

            <div className="rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-right">
              <p className="text-[11px] font-medium text-orange-600">
                Total Tasks
              </p>

              <p className="text-lg font-bold text-orange-700">
                {totalStatusTasks}
              </p>
            </div>
          </div>

          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            {statusSummaryCards.map((card) => {
              const Icon = card.Icon;

              return (
                <div
                  key={card.label}
                  className="rounded-2xl border p-4"
                  style={{
                    backgroundColor: card.bg,
                    borderColor: card.bg,
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-gray-600">
                        {card.label}
                      </p>

                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {card.value}
                      </p>

                      <p className="mt-1 text-xs text-gray-600">
                        {card.percentage}% of total
                      </p>
                    </div>

                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-white"
                      style={{ color: card.color }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {statusDisplayRows.length === 0 ? (
            <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
              <div className="text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-gray-400" />

                <p className="text-sm font-medium text-gray-700">
                  No status data found
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Try changing the selected date range
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {statusDisplayRows.map((item) => {
                const Icon = item.tone.Icon;

                return (
                  <div
                    key={item.taskStatus}
                    className="rounded-2xl border bg-white p-4 transition hover:shadow-sm"
                    style={{
                      borderColor: item.tone.border,
                    }}
                  >
                    <div className="mb-3 flex items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                          style={{
                            backgroundColor: item.tone.bg,
                            color: item.tone.color,
                          }}
                        >
                          <Icon className="h-5 w-5" />
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900">
                            {formatStatusLabel(item.taskStatus)}
                          </p>

                          <p className="text-xs text-gray-500">
                            {item.tone.label}

                            {topStatus?.taskStatus === item.taskStatus
                              ? " • Highest volume"
                              : ""}
                          </p>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {item.taskCount}
                        </p>

                        <p
                          className="text-xs font-medium"
                          style={{ color: item.tone.color }}
                        >
                          {item.percentage}%
                        </p>
                      </div>
                    </div>

                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: item.tone.color,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* TOP SHOPS */}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Store className="h-5 w-5 text-orange-600" />
              Top Shops
            </h3>

            <span className="text-xs text-gray-500">By task count</span>
          </div>

          <div className="space-y-3">
            {topShops.map((shop, index) => (
              <div
                key={`${shop.shopCode}-${index}`}
                className="rounded-xl border p-3 transition hover:bg-orange-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      #{index + 1} {shop.shopName}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      Code: {shop.shopCode}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {shop.taskCount}
                    </p>

                    <p className="text-xs text-gray-500">
                      {getShopShare(shop.taskCount)}%
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 h-64">
            <Pie
              data={restaurantPieChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* =====================================================
          TOP DRIVERS + CHART
      ===================================================== */}

      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Trophy className="h-5 w-5 text-orange-600" />
              Top Drivers
            </h3>

            <span className="text-xs text-gray-500">Highest task volume</span>
          </div>

          <div className="space-y-3">
            {topDrivers.map((driver, index) => (
              <div
                key={`${driver.driverCode}-${index}`}
                className="rounded-xl border p-3 transition hover:bg-orange-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      #{index + 1} {driver.driverName}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      {driver.driverCode} • {driver.driverMobile}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {driver.taskCount}
                    </p>

                    <p className="text-xs text-gray-500">
                      {getDriverShare(driver.taskCount)}%
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Truck className="h-5 w-5 text-orange-600" />
              Driver Contribution
            </h3>

            <span className="text-xs text-gray-500">Top 5 comparison</span>
          </div>

          <div className="h-80">
            <Bar
              data={driverBarChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,

                plugins: {
                  legend: {
                    display: false,
                  },
                },

                scales: {
                  x: {
                    ticks: {
                      color: "#475569",
                    },

                    grid: {
                      display: false,
                    },
                  },

                  y: {
                    ticks: {
                      color: "#475569",
                    },

                    grid: {
                      color: "#e5e7eb",
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* =====================================================
          FULL TABLES
      ===================================================== */}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* SHOPS */}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                All Shops Ranking
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                {filteredShopRows.length} visible of {safeRestaurantData.length}{" "}
                shops
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={shopSearch}
                  onChange={(e) => setShopSearch(e.target.value)}
                  placeholder="Search shop or code..."
                  className="h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 sm:w-52"
                />
              </div>

              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <select
                  value={shopSortBy}
                  onChange={(e) => setShopSortBy(e.target.value)}
                  className="h-10 w-full rounded-xl border bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 sm:w-44"
                >
                  <option value="tasks_desc">Tasks: High to Low</option>

                  <option value="tasks_asc">Tasks: Low to High</option>

                  <option value="name_asc">Name: A to Z</option>

                  <option value="name_desc">Name: Z to A</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-orange-50">
                <tr className="border-b text-left">
                  <th className="px-3 py-3 font-semibold text-gray-700">#</th>

                  <th className="px-3 py-3 font-semibold text-gray-700">
                    Shop
                  </th>

                  <th className="px-3 py-3 font-semibold text-gray-700">
                    Code
                  </th>

                  <th className="px-3 py-3 text-right font-semibold text-gray-700">
                    Tasks
                  </th>

                  <th className="px-3 py-3 text-right font-semibold text-gray-700">
                    Share
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredShopRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No shop data found
                    </td>
                  </tr>
                ) : (
                  filteredShopRows.map((shop, index) => (
                    <tr
                      key={`${shop.shopCode}-${index}`}
                      className="border-b last:border-b-0 hover:bg-orange-50/40"
                    >
                      <td className="px-3 py-3">{index + 1}</td>

                      <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-900">
                        {shop.shopName}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                        {shop.shopCode}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-right font-semibold">
                        {shop.taskCount}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-right text-gray-500">
                        {getShopShare(shop.taskCount)}%
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* DRIVERS */}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                All Drivers Ranking
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                {filteredDriverRows.length} visible of{" "}
                {safeDriverTaskAnalytics.length} drivers
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  placeholder="Search driver, code, mobile..."
                  className="h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 sm:w-56"
                />
              </div>

              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <select
                  value={driverSortBy}
                  onChange={(e) => setDriverSortBy(e.target.value)}
                  className="h-10 w-full rounded-xl border bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 sm:w-44"
                >
                  <option value="tasks_desc">Tasks: High to Low</option>

                  <option value="tasks_asc">Tasks: Low to High</option>

                  <option value="name_asc">Name: A to Z</option>

                  <option value="name_desc">Name: Z to A</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-orange-50">
                <tr className="border-b text-left">
                  <th className="px-3 py-3 font-semibold text-gray-700">#</th>

                  <th className="px-3 py-3 font-semibold text-gray-700">
                    Driver
                  </th>

                  <th className="px-3 py-3 font-semibold text-gray-700">
                    Code
                  </th>

                  <th className="px-3 py-3 font-semibold text-gray-700">
                    Mobile
                  </th>

                  <th className="px-3 py-3 text-right font-semibold text-gray-700">
                    Tasks
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredDriverRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No driver data found
                    </td>
                  </tr>
                ) : (
                  filteredDriverRows.map((driver, index) => (
                    <tr
                      key={`${driver.driverCode}-${index}`}
                      className="border-b last:border-b-0 hover:bg-orange-50/40"
                    >
                      <td className="px-3 py-3">{index + 1}</td>

                      <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-900">
                        {driver.driverName}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                        {driver.driverCode}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                        {driver.driverMobile}
                      </td>

                      <td className="whitespace-nowrap px-3 py-3 text-right font-semibold">
                        {driver.taskCount}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default PackkyReportsTab;
